import { sendRequest } from "@api/clients/axios";
import { Alert, Box, Button, Stack, TextField, Typography } from "@mui/material";
import {
  canReviewDocumentDates,
  type DocumentDateInput,
  type DocumentLifecycleView,
  documentDateRuleLabelFr,
  documentDateValueSchema,
  documentLastValidDay,
  documentLifecycleViewSchema,
  documentValidityLabelFr,
  evaluateDocumentValidity,
  expiryFromDocumentDate,
  getDocumentDateField,
  initialDocumentReviewDate,
  needsDocumentDateReview,
} from "@repo/types";
import { formatDate, formatDateTime } from "@repo/utils/date";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { useState } from "react";
import { RejectionReasonFields } from "@/components/common/RejectionModal";
import { ConfirmationDialogButton } from "@/components/Driver/DriverRequestDetails/confirmation-dialog-button";
import type { ReasonObject } from "@/libs/generated/types";
import { DocumentExpiryTestPanel } from "./DocumentExpiryTestPanel";
import { documentCancellationReasonKeys } from "./types";

const eventLabels: Record<string, string> = {
  EXPIRY_REMINDER_PUSH_J30: "J-30 · notification push",
  EXPIRY_REMINDER_EMAIL_J14: "J-14 · e-mail",
  EXPIRY_REMINDER_SMS_J7: "J-7 · SMS",
  EXPIRED_PAUSE: "Expiration · mise en pause",
  EXPIRY_PAUSE_LIFTED: "Reprise des missions",
};

/** A `YYYY-MM-DD` calendar date as dd/MM/yyyy. */
const formatDay = (date: string) => formatDate(date, { fallback: "" });

type Props = {
  versionId: number;
  isPending: boolean;
  onApprove: () => Promise<unknown>;
  onReject: (reason: ReasonObject) => Promise<unknown>;
  onChanged: (document: DocumentLifecycleView) => void;
  actionError?: string;
};

/** Dates, validity and review decision for one uploaded version. */
export function DocumentReviewPanel({
  versionId,
  isPending,
  onApprove,
  onReject,
  onChanged,
  actionError,
}: Props) {
  const client = useQueryClient();
  const key = ["document-lifecycle", versionId];
  const [date, setDate] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectNote, setRejectNote] = useState("");
  const [deciding, setDeciding] = useState(false);

  const query = useQuery({
    queryKey: key,
    queryFn: async () => {
      const response = await sendRequest<undefined, unknown>(
        "get",
        `documents/${versionId}/lifecycle`
      );
      return documentLifecycleViewSchema.parse(response.data);
    },
  });
  const refresh = async (result: DocumentLifecycleView) => {
    client.setQueryData(key, result);
    // Saving a date also updates the document's other sides (licence front/back).
    await client.invalidateQueries({
      queryKey: ["document-lifecycle"],
      predicate: (query) => query.queryKey[1] !== versionId,
    });
    await client.invalidateQueries({ queryKey: ["notifications"] });
    onChanged(result);
  };
  const save = useMutation({
    mutationFn: async (input: DocumentDateInput) => {
      const response = await sendRequest<DocumentDateInput, unknown>(
        "put",
        `documents/${versionId}/dates`,
        input
      );
      return documentLifecycleViewSchema.parse(response.data);
    },
    onSuccess: async (result) => {
      setDate(null);
      await refresh(result);
    },
  });

  const doc = query.data;
  if (!doc) {
    return query.error ? (
      <Alert
        severity="error"
        action={<Button onClick={() => void query.refetch()}>{t("document.review.retry")}</Button>}
      >
        {query.error instanceof Error ? query.error.message : "Chargement impossible"}
      </Alert>
    ) : (
      <Typography variant="body2" color="text.secondary">
        Chargement…
      </Typography>
    );
  }

  const now = new Date();
  const dateField = getDocumentDateField(doc.name);
  const editable = canReviewDocumentDates(doc);
  const reviewedDate = initialDocumentReviewDate(doc);
  const selectedDate = date ?? reviewedDate;
  const dateValid = documentDateValueSchema.safeParse(selectedDate).success;
  const calculated = dateValid ? expiryFromDocumentDate(doc.name, selectedDate) : null;
  const dateChanged = selectedDate !== (doc.review?.source === "ADMIN" ? doc.review.date : "");
  const mustSaveDate = editable && (needsDocumentDateReview(doc) || dateChanged);
  const busy = save.isPending || deciding;
  const validity = evaluateDocumentValidity(doc.name, doc.expiresAt, now);
  const error = actionError ?? (save.error instanceof Error ? save.error.message : undefined);

  const decide = async (action: () => Promise<unknown>) => {
    setDeciding(true);
    try {
      await action();
    } finally {
      setDeciding(false);
    }
  };
  const approve = () =>
    decide(async () => {
      if (mustSaveDate) {
        await save.mutateAsync({ date: selectedDate, expectedUpdatedAt: doc.updatedAt });
      }
      await onApprove();
    });
  const reject = () =>
    decide(async () => {
      await onReject({ reason: rejectReason, additionalInfo: rejectNote });
      setRejecting(false);
      setRejectReason("");
      setRejectNote("");
    });

  return (
    <Stack spacing={1.5} sx={{ "&, & *": { textTransform: "none" } }}>
      <Box>
        <Typography
          variant="subtitle2"
          color={validity.state === "EXPIRED" ? "error.main" : undefined}
        >
          {documentValidityLabelFr(doc.name, doc.expiresAt, now)}
        </Typography>
        {doc.expiresAt && (
          <Typography variant="body2" color="text.secondary">
            Valable jusqu’au {formatDay(documentLastValidDay(doc.expiresAt))}
          </Typography>
        )}
        {doc.driverPaused && (
          <Typography variant="body2" color="error.main">
            Compte en pause documentaire
          </Typography>
        )}
      </Box>

      {!doc.isCurrent && doc.approvedVersion && (
        <Alert severity="info">
          La version approuvée reste en vigueur jusqu’à la décision :{" "}
          {documentValidityLabelFr(doc.name, doc.approvedVersion.expiresAt, now).toLowerCase()}.
        </Alert>
      )}

      {editable && dateField && (
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            {documentDateRuleLabelFr(dateField)}
          </Typography>
          <TextField
            type="date"
            size="small"
            label={`${dateField.label} vérifiée`}
            value={selectedDate}
            error={selectedDate !== "" && !dateValid}
            onChange={(event) => setDate(event.target.value)}
            helperText={
              doc.suggestedDate
                ? `Saisie du chauffeur : ${formatDay(doc.suggestedDate)}. Vérifiez-la sur le fichier.`
                : "Aucune date saisie par le chauffeur."
            }
            slotProps={{ inputLabel: { shrink: true } }}
          />
          {calculated && (
            <Typography variant="body2">
              → Valable jusqu’au {formatDay(documentLastValidDay(calculated))}
            </Typography>
          )}
          {doc.review && (
            <Typography variant="caption" color="text.secondary">
              {doc.review.source === "SIMULATION" ? "Simulation" : "Dates vérifiées"} par{" "}
              {doc.review.actor} · {formatDateTime(doc.review.recordedAt)}
            </Typography>
          )}
        </Stack>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {isPending && rejecting && (
        <RejectionReasonFields
          reason={rejectReason}
          onReasonChange={setRejectReason}
          additionalInfo={rejectNote}
          onAdditionalInfoChange={setRejectNote}
          reasonLabel={t("document.rejectReason.label")}
          reasonOptions={documentCancellationReasonKeys.map((reason) => ({
            value: reason,
            label: t(`document.rejectReason.${reason}`),
          }))}
          additionalInfoLabel={t("document.review.additionalInfo")}
        />
      )}

      {isPending ? (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          {rejecting ? (
            <>
              <Button variant="outlined" onClick={() => setRejecting(false)} disabled={busy}>
                {t("common.actions.cancel")}
              </Button>
              <Button
                variant="contained"
                color="error"
                disabled={busy || !rejectReason}
                onClick={() => void reject()}
              >
                {t("common.reject")}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                color="error"
                disabled={busy}
                onClick={() => setRejecting(true)}
              >
                {t("common.reject")}
              </Button>
              <Box sx={{ flex: 1 }}>
                <ConfirmationDialogButton
                  buttonText={dateField ? "Vérifier les dates et approuver" : "common.accept"}
                  buttonColor="success.main"
                  title="common.accept"
                  description={
                    calculated
                      ? `Cette version deviendra le document en vigueur, valable jusqu’au ${formatDay(documentLastValidDay(calculated))}.`
                      : "common.accept-document-description"
                  }
                  actionIsLoading={busy}
                  disabled={busy || (!!dateField && !calculated)}
                  onConfirm={() => void approve()}
                />
              </Box>
            </>
          )}
        </Stack>
      ) : (
        editable &&
        doc.isCurrent && (
          <Button
            variant="outlined"
            disabled={busy || !calculated || !dateChanged}
            onClick={() => save.mutate({ date: selectedDate, expectedUpdatedAt: doc.updatedAt })}
          >
            Corriger les dates
          </Button>
        )
      )}

      {/* Dev-only audit trail: raw outbox events and their notification rows. */}
      {import.meta.env.DEV && (doc.events.length > 0 || doc.history.length > 0) && (
        <details>
          <summary>
            <Typography variant="caption" component="span">
              Historique ({doc.events.length + doc.history.length})
            </Typography>
          </summary>
          {doc.events.map((event) => (
            <Typography key={event.id} variant="caption" display="block">
              {eventLabels[event.event] ?? event.event} :{" "}
              {event.lastError ? event.lastError : event.processedAt ? "Traité" : "En attente"}
              {event.deliveries
                .map((delivery) => ` · ${delivery.channel}: ${delivery.status}`)
                .join("")}
            </Typography>
          ))}
          {doc.history.map((entry, index) => (
            <Typography key={`${entry.recordedAt}-${index}`} variant="caption" display="block">
              {formatDateTime(entry.recordedAt)} · {entry.actor} ·{" "}
              {entry.source === "SIMULATION" ? "Simulation" : formatDay(entry.date)} → valable
              jusqu’au {formatDay(documentLastValidDay(entry.expiresAt))}
            </Typography>
          ))}
        </details>
      )}

      {import.meta.env.DEV && editable && doc.isCurrent && (
        <details>
          <summary>
            <Typography variant="caption" component="span">
              Test du cycle de vie (développement)
            </Typography>
          </summary>
          <Box sx={{ pt: 1.5 }}>
            <DocumentExpiryTestPanel doc={doc} onProcessed={refresh} />
          </Box>
        </details>
      )}
    </Stack>
  );
}
